/**
 * Shared date formatting utility for frontend.
 */

export function formatDate(
  dateInput: string | Date | number | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!dateInput) return 'N/A';

  const date = typeof dateInput === 'object' && dateInput instanceof Date
    ? dateInput
    : new Date(dateInput);

  if (isNaN(date.getTime())) return 'N/A';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  return date.toLocaleDateString('en-US', options ?? defaultOptions);
}

export function formatLongDate(dateInput: string | Date | number | null | undefined): string {
  return formatDate(dateInput, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Clock time only. Must not route through `formatDate`: `toLocaleDateString`
 * re-adds the numeric year/month/day defaults whenever the caller requests no
 * date components, so an hour/minute-only options bag came back as a full
 * date-time ("9/2/2026, 02:37 PM") and printed the date twice wherever a
 * caller stacked it under `formatDate`.
 */
export function formatTime(dateInput: string | Date | number | null | undefined): string {
  if (!dateInput) return 'N/A';

  const date = typeof dateInput === 'object' && dateInput instanceof Date
    ? dateInput
    : new Date(dateInput);

  if (isNaN(date.getTime())) return 'N/A';

  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function formatDateTime(dateInput: string | Date | number | null | undefined): string {
  return formatDate(dateInput, { 
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}
